/**
 * Browser Deep-Link Validator (Phase 2 / PRE-212)
 *
 * Calls the `validateBrowserDeepLink` Cloud Function (HTTP shim) to
 * verify that a JWT in the page URL is still valid before letting the
 * AgentActivity page render. If the token is invalid, expired, or the
 * signing key is missing in GCP Secret Manager, the helper returns
 * `{ valid: false, reason }` and the page shows the "link expired" UI.
 *
 * The validator endpoint is sibling to `mintBrowserDeepLink`. Project
 * id is hard-coded to match `functions/src/browserDeepLink.ts`.
 */

const PROJECT_ID = 'tensile-tenure-453409-k3';
const REGION = 'us-central1';

export const VALIDATE_DEEP_LINK_URL = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/validateBrowserDeepLink`;

export interface DeepLinkValidationResult {
  valid: boolean;
  /**
   * Stable machine-readable reason on failure. Examples:
   *   - 'signing-key-not-configured' (Cloud Function couldn't load the secret)
   *   - 'expired'
   *   - 'malformed token'
   *   - 'signature mismatch'
   *   - 'missing-token'
   *   - 'network'
   */
  reason?: string;
  sessionId?: string;
  uid?: string;
  expiresAt?: number;
}

export interface ValidateOptions {
  /** Fetch override for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** URL override for tests. Defaults to VALIDATE_DEEP_LINK_URL. */
  endpoint?: string;
}

export async function validateDeepLinkToken(
  token: string,
  opts: ValidateOptions = {}
): Promise<DeepLinkValidationResult> {
  if (!token) return { valid: false, reason: 'missing-token' };
  const fetcher =
    opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
  if (!fetcher) {
    return { valid: false, reason: 'fetch unavailable' };
  }
  const url = opts.endpoint ?? VALIDATE_DEEP_LINK_URL;
  try {
    const res = await fetcher(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      // ignore parse error
    }
    const valid = body.valid === true || body.ok === true;
    return {
      valid,
      reason: valid ? undefined : (body.reason as string | undefined) || 'invalid',
      sessionId: body.sessionId as string | undefined,
      uid: body.uid as string | undefined,
      expiresAt: body.expiresAt as number | undefined,
    };
  } catch (err) {
    return {
      valid: false,
      reason: err instanceof Error ? err.message : 'network',
    };
  }
}
